import React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import {
  compile,
  createBreadcrumbWithI18n,
} from '~/apps/ninesegsAnalyzer/routes/paths'
import {
  PageContainer,
  type PageContainerProps,
} from '~/components/elements/PageContainer'
import { SurveySettingsForm } from '~/components/features/SurveySettingsForm'
import { useInvalidateCommonQuery } from '~/hooks/useCommonQuery'
import { useInvalidateCurrentBrandAccountSurveysQuery } from '~/hooks/useCurrentBrandAccountSurveysQuery'
import { useLogEvent } from '~/hooks/useLogEvent'
import { useNotify } from '~/hooks/useNotify'
import {
  useInvalidateSurveyForSettingsQuery,
  useSurveyForSettingsQueryData,
} from '~/hooks/useSurveyForSettingsQuery'
import { exists } from '~/utils'

import { useRemoveSurveyMutation } from '../hooks/useRemoveSurveyMutation'
import { useSurveyId } from '../hooks/useSurveyId'
import { useUpdateSurveyMutation } from '../hooks/useUpdateSurveyMutation'
import { pathKey } from '../utils'

export const SurveySettings = () => {
  const { t } = useTranslation()
  const surveyId = useSurveyId()

  const createBreadcrumb = React.useMemo(() => createBreadcrumbWithI18n(t), [t])
  const breadcrumbs = React.useMemo<PageContainerProps['breadcrumbs']>(
    () => [
      createBreadcrumb('top', {}),
      createBreadcrumb('settings'),
      createBreadcrumb(pathKey, { surveyId }),
    ],
    [createBreadcrumb, surveyId],
  )

  const survey = useSurveyForSettingsQueryData()

  const notify = useNotify()
  const logEvent = useLogEvent()
  const invalidateCommonQuery = useInvalidateCommonQuery()
  const invalidateCurrentBrandAccountSurveysQuery =
    useInvalidateCurrentBrandAccountSurveysQuery()
  const invalidateSurveyForSettingsQuery = useInvalidateSurveyForSettingsQuery()
  const updateSurveyMutation = useUpdateSurveyMutation({
    onSuccess: () => {
      notify({ content: t('survey_saved'), style: 'success' })
      logEvent('Save general survey information')

      invalidateCommonQuery()
      invalidateSurveyForSettingsQuery()
    },
  })

  const navigate = useNavigate()
  const removeSurveyMutation = useRemoveSurveyMutation({
    onSuccess: () => {
      notify({ content: t('survey_deleted'), style: 'success' })
      invalidateCommonQuery()
      invalidateCurrentBrandAccountSurveysQuery()

      const brandAccountId = survey?.brandAccountId
      if (exists(brandAccountId)) {
        navigate(compile('createSurvey', { brandAccountId }))
      }
    },
  })

  return (
    <PageContainer
      breadcrumbs={breadcrumbs}
      executing={
        updateSurveyMutation.isPending || removeSurveyMutation.isPending
      }
    >
      <SurveySettingsForm
        survey={survey}
        onRemove={removeSurveyMutation.mutate}
        onSave={updateSurveyMutation.mutate}
      />
    </PageContainer>
  )
}
